import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { StatsOut } from '../api/types';
import type { Difficulty } from '../sudoku';
import { formatMs } from '../components/Timer';
import { useOnline } from '../hooks/useOnline';
import { formatSeen, lastSeen, markSeen } from '../lib/lastSeen';

const ORDER: Difficulty[] = ['easy', 'medium', 'hard', 'expert'];
const SEEN_KEY = 'stats';

export function Stats() {
  const [data, setData] = useState<StatsOut | null>(null);
  const [error, setError] = useState(false);
  const online = useOnline();

  useEffect(() => {
    let cancelled = false;
    api<StatsOut>('/stats')
      .then((d) => {
        if (cancelled) return;
        markSeen(SEEN_KEY);
        setData(d);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <p className="notice">
        {online ? 'Stats need a connection.' : 'You’re offline — stats aren’t downloaded yet.'}
      </p>
    );
  }
  if (!data) return <p className="notice">Loading stats…</p>;

  return (
    <section className="stats">
      <h1>Your stats</h1>
      {!online && (
        <p className="notice offline-note">
          Offline — showing the last update from {formatSeen(lastSeen(SEEN_KEY))}.
        </p>
      )}
      <div className="stat-cards">
        <div className="stat-card">
          <span className="stat-num">{data.current_streak}</span>
          <span className="stat-label">Current streak</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{data.longest_streak}</span>
          <span className="stat-label">Longest streak</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{data.total_solved}</span>
          <span className="stat-label">Daily solved</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{data.hints_used_avg.toFixed(1)}</span>
          <span className="stat-label">Avg hints</span>
        </div>
      </div>

      <h2>By difficulty</h2>
      <table className="by-difficulty">
        <thead>
          <tr>
            <th>Difficulty</th>
            <th>Solved</th>
            <th>Best</th>
            <th>Average</th>
          </tr>
        </thead>
        <tbody>
          {ORDER.map((d) => {
            const s = data.per_difficulty[d];
            return (
              <tr key={d}>
                <td className="diff-tag">{d}</td>
                <td>{s.count}</td>
                <td>{s.best_ms != null ? formatMs(s.best_ms) : '—'}</td>
                <td>{s.avg_ms != null ? formatMs(s.avg_ms) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
